import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = ['client', 'therapist', 'admin'] 
}) => {
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    let didRedirect = false;
    const verifyAccess = async () => {
      try {
        // Get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        
        if (!session) {
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          await supabase.auth.signOut();
          localStorage.setItem('redirectAfterLogin', window.location.pathname);
          didRedirect = true;
          navigate('/auth');
          return;
        }

        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (roleError) {
          console.error('Error fetching user role:', roleError);
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          await supabase.auth.signOut();
          didRedirect = true;
          navigate('/auth');
          return;
        }

        if (!roleData || !roleData.role) {
          console.error('No role found for user');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          await supabase.auth.signOut();
          didRedirect = true;
          navigate('/auth');
          return;
        }

        if (!allowedRoles.includes(roleData.role)) {
          console.error('User role not allowed:', roleData.role);
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          await supabase.auth.signOut();
          didRedirect = true;
          navigate('/auth');
          return;
        }

        localStorage.setItem('userRole', roleData.role);
        localStorage.setItem('userId', session.user.id);

        const currentPath = window.location.pathname;
        if (currentPath === '/' || currentPath === '/dashboard') {
          const redirectPath = roleData.role === 'therapist' ? '/therapist' : '/journal';
          navigate(redirectPath);
        }

      } catch (error) {
        console.error('Error verifying access:', error);
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        await supabase.auth.signOut();
        didRedirect = true;
        navigate('/auth');
      } finally {
        if (!didRedirect) setIsVerifying(false);
      }
    };

    verifyAccess();
  }, [navigate, allowedRoles]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
